import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { api, getImpersonationToken, setImpersonationToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Capture once whether the page was opened with an auth hash fragment.
  // Supabase JS will parse this asynchronously (detectSessionInUrl=true),
  // so we must wait for it before deciding the user is "not logged in".
  const [initialHasAuthHash] = useState(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hash || "";
    return h.includes("access_token=") || h.includes("token_hash=");
  });

  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [appMode, setAppMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whitelistError, setWhitelistError] = useState(null);
  const [syncError, setSyncError] = useState(false);
  // Settled = either the hash was parsed (session emitted) or we gave up
  // waiting after a timeout. Until settled, we keep loading=true so
  // <AuthCallback> doesn't bounce to /login prematurely.
  const [hashParseSettled, setHashParseSettled] = useState(!initialHasAuthHash);
  // RELOAD-01. Menandai `supabase.auth.getSession()` sudah selesai menjawab —
  // BUKAN "ada sesi", melainkan "sudah diperiksa". Dua hal yang berbeda, dan
  // membedakannya adalah inti perbaikan ini.
  //
  // `hashParseSettled` TIDAK bisa dipakai untuk ini. Saat reload biasa (URL
  // tanpa hash) ia sudah `true` sejak render pertama — memang benar begitu,
  // sebab tidak ada hash yang perlu diurai. Yang belum benar saat itu adalah
  // `session`: ia masih `null` bawaan karena `getSession()` async dan belum
  // sempat membaca sesi yang sah dari localStorage. Efek di bawah membaca
  // pasangan (session=null, hashParseSettled=true) sebagai "tidak login",
  // menyetel loading=false, dan ProtectedRoute melempar ke /login — padahal
  // sesinya ada dan valid. Ini yang bikin F5 = logout paksa.
  const [initialSessionChecked, setInitialSessionChecked] = useState(false);

  // Bertindak Atas Nama (K140) — fitur permanen; state + tiga fungsi di bawah
  // adalah sambungannya ke backend/dev_impersonation.py.
  const [impersonation, setImpersonation] = useState(null);

  const syncProfile = useCallback(async () => {
    try {
      setWhitelistError(null);
      setSyncError(false);
      const res = await api.post("/auth/sync");
      setProfile(res.data.profile);
      setAppMode(res.data.app_mode);

      // DEV ONLY: `/auth/sync` sengaja selalu mengembalikan profil ASLI
      // (ia membaca whitelist). Profil EFEKTIF saat menyamar hanya bisa
      // datang dari `/me`, yang lewat `require_profile` di backend. Tanpa
      // langkah ini, penyamaran hilang tiap kali halaman di-reload.
      if (getImpersonationToken()) {
        try {
          const me = await api.get("/me");
          setProfile(me.data.profile);
          setAppMode(me.data.app_mode);
          setImpersonation(me.data.impersonation || null);
          // Token basi/ditolak → backend menjawab sebagai Owner biasa.
          // Buang tokennya supaya badge tidak berbohong dan header
          // berhenti dikirim di permintaan berikutnya.
          if (!me.data.impersonation) setImpersonationToken(null);
        } catch {
          setImpersonationToken(null);
          setImpersonation(null);
        }
      } else {
        setImpersonation(null);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        setWhitelistError(detail?.message || "Email Anda belum di-whitelist oleh Owner ETAMS.");
      } else {
        setSyncError(true);
      }
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let timeoutId;
    // `.catch()` di ujung rantai ini WAJIB, dan ia menutup regresi yang
    // dilahirkan perbaikan RELOAD-01 itu sendiri. Sebelum gerbang
    // `initialSessionChecked` ada, `getSession()` yang MENOLAK berakhir di
    // /login — jelek, tapi jalan. Sesudah gerbang ada, penolakan berarti
    // gerbangnya tidak pernah dibuka dan aplikasi macet permanen di layar
    // "Memuat…". Menukar logout paksa dengan layar beku bukan perbaikan.
    //
    // Sengaja HANYA menandai "sudah diperiksa", bukan menyentuh `session`
    // atau `hashParseSettled`: gagal membaca sesi artinya kita tidak tahu
    // apa-apa, jadi biarkan cabang di bawah yang memutuskan — dan cabang itu
    // akan mengirim orangnya ke /login, persis perilaku sebelum RELOAD-01.
    // Pola yang sama dengan AUTHCALLBACK-01: jalur gagal wajib punya ujung.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setInitialSessionChecked(true);
      if (!data.session && initialHasAuthHash) {
        // Hash not parsed yet — wait for onAuthStateChange or timeout.
        timeoutId = setTimeout(() => setHashParseSettled(true), 5000);
      } else {
        setHashParseSettled(true);
      }
    }).catch(() => setInitialSessionChecked(true));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      // Ditandai di SINI juga, bukan cuma di `.then()` di atas. Kalau event
      // auth mendahului `getSession()` selesai, jalur ini yang membuka gerbang
      // — tanpanya `loading` bisa tertahan `true` sampai `.then()` menyusul.
      setInitialSessionChecked(true);
      setHashParseSettled(true);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    });
    return () => {
      sub.subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [initialHasAuthHash]);

  useEffect(() => {
    // RELOAD-01 — gerbang, dipasang PALING ATAS dan sengaja sebelum cabang
    // mana pun. Selama `getSession()` belum menjawab, `session === null` tidak
    // berarti apa-apa: ia nilai bawaan, bukan jawaban. Menyimpulkan "tidak
    // login" dari nilai bawaan adalah cacatnya. Di sini kita cuma menunggu —
    // `loading` tetap `true`, dan efek ini jalan lagi begitu jawabannya tiba.
    //
    // Logika `hashParseSettled` di bawah TIDAK diubah sedikit pun: jalur OAuth
    // callback tetap menunggu hash selesai diurai persis seperti sebelumnya.
    // Gerbang ini hanya menambah satu syarat DI ATASNYA.
    if (!initialSessionChecked) return;
    if (session) {
      syncProfile().finally(() => setLoading(false));
    } else if (hashParseSettled) {
      setProfile(null);
      setWhitelistError(null);
      setSyncError(false);
      setLoading(false);
    }
    // else: still waiting for Supabase to finish parsing the URL hash —
    // keep loading=true so the callback page shows "Memproses login...".
  }, [session, syncProfile, hashParseSettled, initialSessionChecked]);

  const refreshAppMode = useCallback(async () => {
    try {
      const res = await api.get("/app-mode");
      setAppMode(res.data);
    } catch {
      /* noop */
    }
  }, []);

  const signOut = useCallback(async () => {
    setImpersonationToken(null);
    await supabase.auth.signOut();
    setProfile(null);
    setSession(null);
    setWhitelistError(null);
    setSyncError(false);
  }, []);

  // --- BERTINDAK ATAS NAMA (K140, 1 September 2026) ---
  //
  // ⛔ KOMENTAR LAMA DI SINI BERBUNYI "DEV ONLY: Role Switcher UAT". Itu
  //    DICABUT K140 — ia fitur permanen berpagar sekarang, bukan perkakas
  //    uji yang dihapus sebelum Production.
  //
  // ⛔ `alasan` WAJIB, dan ketiadaannya sempat MEMATAHKAN tombol ini.
  //    Backend mulai mewajibkannya 1 September 2026 (K140 butir 6);
  //    fungsi ini nol ikut diperbarui, jadi setiap pergantian peran dijawab
  //    `400 DELEGASI_ALASAN_WAJIB`. Test backend lulus karena helper-nya
  //    mengoper alasan — sisi frontend nol pernah dilewati satu pun test.
  //
  //    Pelajarannya: kontrak yang berubah wajib ditelusuri ke SELURUH
  //    pemanggil, bukan cuma yang muncul di test.
  const startImpersonation = useCallback(async (roleCode, regionId, alasan, storeId) => {
    const res = await api.post("/auth/_dev_impersonate", {
      role_code: roleCode,
      region_id: regionId || null,
      // T15: peran terikat cabang (CS, Admin Produksi, Admin Gudang) membawa CABANG juga.
      store_id: storeId || null,
      alasan: alasan || "",
    });
    setImpersonationToken(res.data.token);
    // Reload penuh, bukan sekadar refresh profil: setiap halaman yang
    // sedang terbuka memegang data yang diambil dengan role lama. Muat
    // ulang menjamin tidak ada sisa tampilan dari role sebelumnya.
    window.location.reload();
  }, []);

  const stopImpersonation = useCallback(async () => {
    // Urutan ini disengaja: token DIBUANG LEBIH DULU. Apa pun yang terjadi
    // setelah baris ini — backend menolak, jaringan mati, token sudah
    // kedaluwarsa — penyamaran sudah berakhir. Tombol "Kembali jadi Owner"
    // tidak boleh bergantung pada endpoint yang mungkin gagal, karena
    // kalau gagal orangnya terjebak dalam penyamaran yang tak bisa dibatalkan.
    const tok = getImpersonationToken();
    setImpersonationToken(null);
    if (tok) {
      try {
        await Promise.race([
          api.post("/auth/_dev_impersonate/stop", {}, {
            headers: { "X-ETAMS-Impersonate": tok },
          }),
          new Promise((r) => setTimeout(r, 1500)),
        ]);
      } catch {
        /* jejak audit best-effort — tidak boleh menahan jalan keluar */
      }
    }
    window.location.reload();
  }, []);
  // --- akhir blok DEV ONLY ---

  // K236 (BUSINESS-115): PEMANTAU — hanya baca. Server menampilkan dirinya ke endpoint BACA sebagai Owner dan
  // menolak SEMUA tulisan (403 PEMANTAU_HANYA_MELIHAT). Layar mengikuti: peran tampil = OWNER (menu, rute, angka
  // uang sama dengan Owner), `isPemantau` menyalakan mode HanyaBaca (tombol aksi disembunyikan, saklar terkunci).
  // `peranAsli` untuk yang wajib tahu peran sungguhan (pita, Bertindak Atas Nama).
  const peranAsli = profile?.role?.code || null;
  const isPemantau = peranAsli === "PEMANTAU";
  const profilTampil = isPemantau && profile
    ? { ...profile, role: { ...profile.role, code: "OWNER", name: "Pemantau (hanya melihat)" } }
    : profile;

  const value = {
    session,
    profile: profilTampil,
    peranAsli,
    isPemantau,
    appMode,
    loading,
    whitelistError,
    syncError,
    syncProfile,
    refreshAppMode,
    signOut,
    // Bertindak Atas Nama (K140)
    impersonation,
    startImpersonation,
    stopImpersonation,
    devToolsEnabled: appMode?.dev_tools_enabled === true,
    // ⛔ BENDERA SENDIRI, bukan `dev_tools_enabled`. K140 memisahkan
    //    keduanya: delegasi fitur permanen, dev tools perkakas uji yang
    //    salah satunya (`_dev_reset_to_uat`, dihapus 2 Sep 2026) MENGHAPUS
    //    data simulasi.
    //    Menyatukannya berarti nol bisa menyalakan yang satu tanpa
    //    yang lain.
    delegasiAktif: appMode?.delegasi_aktif === true,
    roleCode: profilTampil?.role?.code || null,
    isOwner: profilTampil?.role?.code === "OWNER",
    isKepalaCabang: profilTampil?.role?.code === "KEPALA_CABANG",
    isCS: profilTampil?.role?.code === "CS",
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
